import { useCallback, useEffect, useId, useState } from 'react';
import {
  declineItineraryRequest,
  fulfilItineraryRequest,
  getAdminItineraryRequests,
} from '../../services/itinerary';
import type {
  ItineraryRequest,
  ItineraryRequestStatus,
} from '../../types/itinerary';
import { getErrorMessage } from '../../utils/errorHandler';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/TextInput';
import { isSafeScheduleUrl } from './scheduleLink';
import { Link } from 'react-router-dom';
import { formatRequestDateRange } from '../dashboard/sections/schedule/eventDateRange';
import { itineraryBuildForRequestUrl } from '../dashboard/sections/schedule/itinerarySetup';
import { useItineraryRequestCount } from '../../hooks/useItineraryRequestCount';
import { AdminPage, AdminPageHeader } from './AdminPage';
import './AdminItineraryRequests.css';

/** The filter values the queue offers. `undefined` means "every status". */
const STATUS_FILTERS: { value: ItineraryRequestStatus | undefined; label: string }[] = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Fulfilled', label: 'Fulfilled' },
  { value: 'Declined', label: 'Declined' },
  { value: undefined, label: 'All' },
];

/** Which inline action, if any, is open on a row. */
type RowAction = { id: string; kind: 'decline' | 'fulfil' } | null;

/**
 * RosterPilot's cross-organization queue of itinerary requests (system_admin only).
 *
 * Every row names the program that asked, the tournament and its dates, and — the part that
 * actually saves staff time — the schedule link as something they can open in one click. Staff can
 * decline a request with a note the program sees, or mark it fulfilled against an event already on
 * that program's calendar for the cases automatic matching cannot see.
 */
export const AdminItineraryRequestsSection = () => {
  const fieldId = useId();
  const [status, setStatus] = useState<ItineraryRequestStatus | undefined>('Pending');
  const [requests, setRequests] = useState<ItineraryRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [action, setAction] = useState<RowAction>(null);
  const [note, setNote] = useState('');
  const [eventId, setEventId] = useState('');
  // The row currently mid-request. Per-row rather than one flag for the list, so declining one
  // request does not grey out every other row's actions.
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  // The nav badge counts what is still pending, so it has to move the moment a request is
  // resolved here — otherwise it keeps advertising a queue the admin has just emptied.
  const { refresh: refreshBadge } = useItineraryRequestCount();

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return getAdminItineraryRequests({ status }, signal)
        .then((found) => {
          setRequests(found);
          setError('');
          setLoading(false);
        })
        .catch((err) => {
          if (signal?.aborted) return;
          setError(getErrorMessage(err, "Couldn't load the itinerary request queue."));
          setLoading(false);
        });
    },
    [status]
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const closeAction = () => {
    setAction(null);
    setNote('');
    setEventId('');
    setActionError('');
  };

  /** Replace the resolved row in place so the queue reflects the decision without a full reload. */
  const applyResolved = (resolved: ItineraryRequest) => {
    setRequests((prev) =>
      status && resolved.status !== status
        ? // It no longer belongs in the filtered view the admin is looking at.
          prev.filter((r) => r.id !== resolved.id)
        : prev.map((r) => (r.id === resolved.id ? resolved : r))
    );
    refreshBadge();
    closeAction();
  };

  const runDecline = async (request: ItineraryRequest) => {
    setBusyId(request.id);
    setActionError('');
    try {
      applyResolved(await declineItineraryRequest(request.id, note));
    } catch (err) {
      setActionError(getErrorMessage(err, "Couldn't decline that request. Try again."));
    } finally {
      setBusyId(null);
    }
  };

  const runFulfil = async (request: ItineraryRequest) => {
    const trimmed = eventId.trim();
    if (!trimmed) {
      setActionError('Enter the calendar event on that program’s calendar.');
      return;
    }
    setBusyId(request.id);
    setActionError('');
    try {
      applyResolved(await fulfilItineraryRequest(request.id, trimmed));
    } catch (err) {
      setActionError(getErrorMessage(err, "Couldn't mark that request fulfilled. Try again."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    // The wrapper only carries the test id (AdminPage takes no data attributes); it draws no box.
    <AdminPage className="itin-queue" data-testid="admin-itinerary-requests">
      <AdminPageHeader
        title="Itinerary requests"
        subtitle={
          <>
            Tournaments programs have asked RosterPilot to build. Open the schedule link before
            building one — a program may have sent a single day rather than the full schedule.
          </>
        }
        actions={
          <div className="itin-queue__filters" role="group" aria-label="Filter by status">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.label}
                type="button"
                className={`itin-queue__filter${status === filter.value ? ' itin-queue__filter--on' : ''}`}
                // aria-current, not aria-pressed: these are four mutually exclusive views of one queue.
                // aria-pressed announces four independent toggles, which is a different control.
                aria-current={status === filter.value ? 'true' : undefined}
                onClick={() => {
                  closeAction();
                  setStatus(filter.value);
                }}
                data-testid={`itinerary-queue-filter-${filter.label.toLowerCase()}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        }
      />

      {error && (
        <div className="admin-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <p className="admin-state">Loading requests…</p>
      ) : requests.length === 0 ? (
        <p className="admin-state" data-testid="itinerary-queue-empty">
          No itinerary requests here.
        </p>
      ) : (
        <ul className="itin-queue__list">
          {requests.map((request) => {
            const open = action?.id === request.id ? action.kind : null;
            const busy = busyId === request.id;
            return (
              <li
                key={request.id}
                className="itin-queue__row"
                data-testid={`itinerary-queue-row-${request.id}`}
              >
                <div className="itin-queue__main">
                  <span className="itin-queue__org">{request.organizationName}</span>
                  <span className="itin-queue__tournament">{request.tournamentName}</span>
                  <span className="itin-queue__dates">
                    {formatRequestDateRange(request.startDateUtc, request.endDateUtc)}
                  </span>
                  {request.locationName && (
                    <span className="itin-queue__meta">📍 {request.locationName}</span>
                  )}
                  {request.requestedByName && (
                    <span className="itin-queue__meta">
                      Requested by {request.requestedByName}
                    </span>
                  )}
                  {request.scheduleUrl &&
                    (isSafeScheduleUrl(request.scheduleUrl) ? (
                      <a
                        className="itin-queue__link"
                        href={request.scheduleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`itinerary-queue-link-${request.id}`}
                      >
                        ↗ Open the schedule
                      </a>
                    ) : (
                      <span className="itin-queue__meta">
                        Schedule link (not openable): {request.scheduleUrl}
                      </span>
                    ))}
                  {request.notes && <p className="itin-queue__notes">{request.notes}</p>}
                  {request.resolutionNote && (
                    <p className="itin-queue__notes">
                      Resolution: {request.resolutionNote}
                    </p>
                  )}
                </div>

                <div className="itin-queue__side">
                  <span
                    className={`itin-queue__status itin-queue__status--${request.status.toLowerCase()}`}
                  >
                    {request.status}
                  </span>
                  {request.status === 'Pending' && (
                    <div className="itin-queue__actions">
                      <button
                        type="button"
                        className="itin-queue__minor"
                        onClick={() => {
                          closeAction();
                          setAction({ id: request.id, kind: 'fulfil' });
                        }}
                        data-testid={`itinerary-queue-fulfil-${request.id}`}
                      >
                        Already built it?
                      </button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          closeAction();
                          setAction({ id: request.id, kind: 'decline' });
                        }}
                        data-testid={`itinerary-queue-decline-${request.id}`}
                      >
                        Decline
                      </Button>
                      {/*
                          "Mark fulfilled" is the manual fallback and reads like one now: it wants a
                          calendar-event GUID pasted in by hand. Build is the path staff actually
                          want — it opens the wizard already knowing the tournament, the schedule
                          link and who asked, and its last step shares the result back to them and
                          closes this row. Kept as a link rather than a button so the wizard can be
                          opened in a new tab, which is what an admin working a queue does.
                      */}
                      <Link
                        className="btn btn--primary itin-queue__build"
                        to={itineraryBuildForRequestUrl({
                          requestId: request.id,
                          organizationId: request.organizationId,
                          organizationName: request.organizationName,
                          tournamentName: request.tournamentName,
                          scheduleUrl: request.scheduleUrl,
                        })}
                        data-testid={`itinerary-queue-build-${request.id}`}
                      >
                        Build itinerary
                      </Link>
                    </div>
                  )}
                </div>

                {open === 'decline' && (
                  <form
                    className="itin-queue__form"
                    data-testid={`itinerary-queue-decline-form-${request.id}`}
                    onSubmit={(e) => {
                      e.preventDefault();
                      void runDecline(request);
                    }}
                  >
                    <label className="itin-queue__label" htmlFor={`${fieldId}-note-${request.id}`}>
                      Note for the program (optional)
                    </label>
                    <textarea
                      id={`${fieldId}-note-${request.id}`}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={2}
                      maxLength={2000}
                      data-testid={`itinerary-queue-decline-note-${request.id}`}
                    />
                    {actionError && (
                      <div className="itin-queue__error" role="alert">
                        {actionError}
                      </div>
                    )}
                    <div className="itin-queue__form-actions">
                      <Button type="button" variant="secondary" onClick={closeAction}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="secondary"
                        className="itin-queue__btn--danger"
                        disabled={busy}
                        data-testid={`itinerary-queue-decline-confirm-${request.id}`}
                      >
                        {busy ? 'Declining…' : 'Decline request'}
                      </Button>
                    </div>
                  </form>
                )}

                {open === 'fulfil' && (
                  <form
                    className="itin-queue__form"
                    data-testid={`itinerary-queue-fulfil-form-${request.id}`}
                    onSubmit={(e) => {
                      e.preventDefault();
                      void runFulfil(request);
                    }}
                  >
                    <TextInput
                      label={`Calendar event id on ${request.organizationName}'s calendar`}
                      id={`${fieldId}-event-${request.id}`}
                      value={eventId}
                      onChange={(e) => setEventId(e.target.value)}
                      placeholder="00000000-0000-0000-0000-000000000000"
                      data-testid={`itinerary-queue-fulfil-event-${request.id}`}
                    />
                    {actionError && (
                      <div className="itin-queue__error" role="alert">
                        {actionError}
                      </div>
                    )}
                    <div className="itin-queue__form-actions">
                      <Button type="button" variant="secondary" onClick={closeAction}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={busy}
                        data-testid={`itinerary-queue-fulfil-confirm-${request.id}`}
                      >
                        {busy ? 'Saving…' : 'Mark fulfilled'}
                      </Button>
                    </div>
                  </form>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </AdminPage>
  );
};
